import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getRetailerInfo } from '../../../../actions/retailerUsersActions';
import { fetchCurrentRetailerUser } from '../../../../actions/actions';
import { SET_CONVERSATION_MESSAGES_DATA, setSelectedConversation } from '../../../../actions/inbox/conversationActions';

/**
 * Hook de UI Layer para efectos globales de pantalla del Inbox
 * 
 * Hook especializado que maneja todos los efectos visuales y de inicialización
 * globales de la pantalla principal del inbox. Implementa el patrón de UI Layer
 * con separación estricta de responsabilidades, encapsulando side-effects que
 * afectan el DOM global y la inicialización de datos del retailer.
 * 
 * @function useInboxScreenUI
 * @category Hook
 * @subcategory UI Layer
 * @module InboxScreenHooks
 * @since 3.1.0
 * 
 * @description
 * Hook autocontenido que gestiona:
 * - **Efectos visuales globales**: Cambio de background color del HTML
 * - **Gestión de banners**: Mostrar/ocultar banner de WhatsApp API QR
 * - **Inicialización Redux**: Dispatch de acciones de retailer y usuario
 * - **Cleanup automático**: Restauración de estado visual al desmontar
 * 
 * @architecture
 * **UI Layer Pattern:**
 * - Solo efectos visuales y de presentación globales
 * - Sin lógica de negocio ni manejo de datos
 * - Autocontenido con dependencias Redux internas
 * - Cleanup automático para prevenir memory leaks
 * 
 * **Separation of Concerns:**
 * - ✅ DOM manipulation (background, banners)
 * - ✅ Screen initialization (Redux actions)
 * - ✅ Visual state management (global styles)
 * - ❌ Business logic (delegado a otros hooks)
 * - ❌ Data fetching (solo dispatch de inicialización)
 * - ❌ Form state (delegado a Form Layer hooks)
 * 
 * @features
 * - ✅ **Global Background**: Cambio automático de color de fondo del HTML
 * - ✅ **Banner Management**: Control de visibilidad del banner WhatsApp QR
 * - ✅ **Redux Initialization**: Dispatch automático de acciones de inicialización
 * - ✅ **Automatic Cleanup**: Restauración de estado al desmontar componente
 * - ✅ **Self-Contained**: Maneja sus propias dependencias Redux
 * - ✅ **Single Responsibility**: Solo efectos globales de pantalla
 * 
 * @performance
 * - **Minimal Dependencies**: Solo depende del dispatcher Redux
 * - **Single useEffect**: Un solo efecto con cleanup completo
 * - **DOM Optimization**: Queries mínimas al DOM para banner
 * - **State Isolation**: No afecta re-renders de componentes hijos
 * 
 * @sideEffects
 * **Mount Effects:**
 * - Cambia `document.querySelector('html').style.backgroundColor` a 'white'
 * - Remueve clase 'd-none-important' del elemento #info-whatsapp-api-qr
 * - Dispatch `getRetailerInfo()` para cargar información del retailer
 * - Dispatch `fetchCurrentRetailerUser()` para cargar usuario actual
 * 
 * **Unmount Effects (Cleanup):**
 * - Restaura `document.querySelector('html').style.backgroundColor` a '#F7F8FD'
 * - Agrega clase 'd-none-important' al elemento #info-whatsapp-api-qr
 * - Dispatch `{ type: 'RESET_TEMPORAL_STATE' }` para limpiar estados temporales
 * 
 * @usage
 * **Uso Recomendado:**
 * - Solo en la pantalla principal `InboxScreenMC`
 * - Al inicio del componente antes de otros hooks
 * - Una sola vez por instancia de pantalla
 * 
 * **Uso NO Recomendado:**
 * - En componentes que no sean pantalla principal
 * - En hooks de otros layers (Data, Actions, Forms)
 * - Múltiples veces en el mismo componente
 * 
 * @dependencies
 * **Redux Actions:**
 * - `getRetailerInfo` - Carga información del retailer actual
 * - `fetchCurrentRetailerUser` - Carga datos del usuario actual
 * - `RESET_TEMPORAL_STATE` - Limpia estados temporales de Redux
 * 
 * **DOM Elements:**
 * - `html` - Elemento raíz para cambio de background
 * - `#info-whatsapp-api-qr` - Banner de información de WhatsApp API
 * 
 * @accessibility
 * - Mantiene contraste adecuado con background blanco
 * - No afecta navegación por teclado
 * - Compatible con lectores de pantalla
 * - Restaura estado visual original al salir
 * 
 * @compatibility
 * - Compatible con todos los navegadores modernos
 * - Funciona con y sin elemento #info-whatsapp-api-qr presente
 * - Robusto ante fallos de DOM queries
 * - Maneja ausencia de elementos DOM gracefully
 * 
 * @returns {void} No retorna valor - Solo ejecuta efectos de inicialización
 * 
 * @example
 * ```jsx
 * import useInboxScreenUI from 'hooks/inbox/ui/useInboxScreenUI';
 * 
 * const InboxScreenMC = () => {
 *   // Inicialización automática de efectos globales
 *   useInboxScreenUI();
 *   
 *   // Resto de hooks y lógica del componente...
 *   
 *   return (
 *     <div className="tw-wrapper">
 *       // Layout del inbox...
 *     </div>
 *   );
 * };
 * ```
 * 
 * @throws {Error} No lanza errores - Manejo robusto de fallos DOM y Redux
 * 
 * @see {@link InboxScreenMC} - Componente principal que usa este hook
 * @see {@link useInboxUI} - Hook de UI para estado interno del inbox
 * @see {@link useInboxBusinessUI} - Hook de lógica de negocio del inbox
 * @see {@link useFiltersScreenUI} - Hook de UI para pantalla de filtros
 * 
 * @since 3.1.0 Creación inicial con efectos básicos
 * @since 3.1.1 Agregado manejo de banner WhatsApp API QR
 * @since 3.1.2 Refactorizado para ser autocontenido sin parámetros
 */
const useInboxScreenUI = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    document.querySelector('html').style.backgroundColor = 'white';
    const infoWhatsappApiQr = document.getElementById('info-whatsapp-api-qr');
    if (infoWhatsappApiQr) infoWhatsappApiQr.classList.remove('d-none-important');

    dispatch(getRetailerInfo());
    dispatch(fetchCurrentRetailerUser());

    return () => {
      document.querySelector('html').style.backgroundColor = '#F7F8FD';
      if (infoWhatsappApiQr) infoWhatsappApiQr.classList.add('d-none-important');
      dispatch(setSelectedConversation(null));
      dispatch({
        type: SET_CONVERSATION_MESSAGES_DATA,
        payload: { data: [] }
      });
      // dispatch({ type: 'RESET_TEMPORAL_STATE' });
    };
  }, [dispatch]);
};

export default useInboxScreenUI;
